import React from "react";
import MathText from "@/components/MathText";
import { Check, X } from "lucide-react";

export default function QuestionCard({ item, value, onChange, result, disabled }) {
  const isMcq = item.type === "mcq" && item.choices;
  return (
    <div data-testid="question-card">
      <div className="text-xs font-semibold uppercase tracking-wider text-[var(--ns-subtle)] mb-2">
        {typeLabel(item.type)}
      </div>
      {item.visual && <Visual visual={item.visual} />}
      <div className="text-xl sm:text-2xl font-head font-semibold text-[var(--ns-text)] mb-5 leading-snug">
        <MathText text={item.prompt} />
      </div>

      {isMcq ? (
        <div className="grid gap-3">
          {item.choices.map((c, i) => {
            const selected = value === c;
            const showCorrect = result && c === result.answer;
            const showWrong = result && selected && !result.correct;
            return (
              <button
                key={i}
                disabled={disabled}
                data-testid={`choice-${i}`}
                onClick={() => onChange(c)}
                className="text-left rounded-[var(--ns-radius)] border-2 px-4 py-3 transition-colors duration-150 flex items-center justify-between"
                style={{
                  minHeight: "var(--ns-touch)",
                  borderColor: showCorrect ? "#2A8555" : showWrong ? "#E05A47" : selected ? "var(--ns-primary)" : "var(--ns-border)",
                  background: showCorrect ? "#E5F3E8" : showWrong ? "#FDECE8" : selected ? "var(--ns-primary-light)" : "var(--ns-surface)",
                }}
              >
                <MathText text={c} />
                {showCorrect && <Check size={18} className="text-[#2A8555]" />}
                {showWrong && <X size={18} className="text-[#E05A47]" />}
              </button>
            );
          })}
        </div>
      ) : (
        <input
          data-testid="answer-input"
          value={value || ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Type your answer…"
          className="w-full rounded-[var(--ns-radius)] border-2 border-[var(--ns-border)] bg-[var(--ns-surface)] px-4 py-3 text-lg focus:outline-none focus:border-[var(--ns-primary)]"
          style={{ minHeight: "var(--ns-touch)" }}
        />
      )}

      {result && (
        <div
          className="mt-4 rounded-[var(--ns-radius)] p-4 text-sm"
          data-testid="question-feedback"
          style={{ background: result.correct ? "#E5F3E8" : "#FDECE8", color: result.correct ? "#1E6B52" : "#B23A28" }}
        >
          <div className="font-semibold mb-1">{result.correct ? "Correct!" : "Not quite."}</div>
          <MathText text={result.explanation} />
        </div>
      )}
    </div>
  );
}

function typeLabel(t) {
  return { mcq: "Multiple choice", numeric: "Numeric entry", short: "Short answer", multi_step: "Multi-step" }[t] || "Question";
}

const Dots = ({ count, color = "var(--ns-primary)" }) => (
  <div className="flex flex-wrap gap-2 justify-center max-w-[280px]">
    {Array.from({ length: count }).map((_, i) => (
      <span key={i} className="rounded-full" style={{ width: 26, height: 26, background: color }} />
    ))}
  </div>
);

function Visual({ visual }) {
  return (
    <div className="mb-5 rounded-[var(--ns-radius)] bg-[var(--ns-surface-subtle)] p-5 grid place-items-center" data-testid="question-visual">
      {visual.type === "dots" && <Dots count={visual.count} />}
      {visual.type === "groups" && (
        <div className="flex items-center gap-4">
          <Dots count={visual.a} color="var(--ns-primary)" />
          <span className="font-head text-3xl font-bold text-[var(--ns-muted)]">+</span>
          <Dots count={visual.b} color="#D96B43" />
        </div>
      )}
    </div>
  );
}
